import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { CheckIcon } from '@heroicons/react/24/solid';

function InvestmentPlans() {
  const plans = [
    {
      name: "Standard Plan",
      price: "$1,000",
      features: [
        "10x Profit in 21 days",
        "2% Welcome bonus",
        "$1,000 earn $10,000",
        "$2,000 earn $20,000",
        "$3,000 earn $30,000",
        "$4,000 earn $40,000"
      ],
      duration: "21 days",
      type: "Basic portfolio",
      support: "24/7 Email Support"
    },
    {
      name: "Premium Plan",
      price: "$5,000",
      features: [
        "15x Profit in 21 days",
        "3% Welcome bonus",
        "$5,000 earn $75,000",
        "$6,000 earn $90,000",
        "$7,000 earn $105,000",
        "$8,000 earn $120,000",
        "$9,000 earn $135,000"
      ],
      duration: "21 days",
      type: "Enhanced portfolio",
      support: "24/7 Priority Chat Support",
      popular: true
    },
    {
      name: "Advanced Plan",
      price: "$10,000",
      features: [
        "20x Profit in 21 days",
        "5% Welcome bonus",
        "$10,000 earn $200,000",
        "$11,000 earn $220,000",
        "$12,000 earn $240,000",
        "$13,000 earn $260,000",
        "$14,000 earn $280,000",
        "VIP Account Manager",
        "Priority Withdrawals",
        "Exclusive Market Analysis"
      ],
      duration: "21 days",
      type: "VIP Wealth Management",
      support: "Dedicated Account Manager"
    }
  ];

  const testimonials = [
    {
      text: "I've tried other platforms, but this one really delivers on transparency and performance. My returns have been impressive!",
      author: "Irene Blimbing"
    },
    {
      text: "The user interface is simple, and I love how my investments are laid out so clearly. Highly recommended!",
      author: "Michael Bedengan"
    },
    {
      text: "The personalized investment updates are a game-changer. I always know exactly where my money stands!",
      author: "David Knox"
    },
    {
      text: "Investing has never been easier! My portfolio has grown steadily, and the dashboard keeps me informed every step of the way.",
      author: "Angelina Kepanjen"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Hero Section */}
      <motion.section 
        className="pt-24 pb-16 bg-primary-900 text-white"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            className="text-4xl md:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Secure Your Financial Future with QuickwealthINVEST
          </motion.h1>
          <motion.p 
            className="text-xl text-primary-100 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            In a rapidly changing world, securing your wealth through cryptocurrency is not just a smart choice – it's essential. Whether you're aiming for a comfortable retirement, building generational wealth, or achieving true financial freedom, our platform empowers you with expert-backed cryptocurrency investment strategies.
          </motion.p>
        </div>
      </motion.section>

      {/* Pricing Plans - Moved to top */}
      <motion.section 
        className="py-16 -mt-16 relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Investment Plans</h2>
            <p className="mt-4 text-lg text-gray-600">Choose your path to financial growth with our transparent, high-yield investment plans</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transform-gpu ${
                  plan.popular ? 'ring-2 ring-primary-500' : ''
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 * index }}
                whileHover={{ 
                  y: -10,
                  scale: 1.02,
                  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                  transition: { duration: 0.2, ease: "easeOut" }
                }}
                whileTap={{ scale: 0.98 }}
              >
                {plan.popular && (
                  <motion.div 
                    className="absolute top-0 right-0 bg-primary-500 text-white px-4 py-1 text-sm font-medium"
                    initial={{ x: 100 }}
                    animate={{ x: 0 }}
                    transition={{ delay: 0.5, type: "spring", stiffness: 100 }}
                  >
                    Most Popular
                  </motion.div>
                )}
                <motion.div className="p-8" whileHover="hover">
                  <motion.h3 
                    className="text-2xl font-bold text-gray-900 mb-4"
                    variants={{ hover: { scale: 1.05 } }}
                  >
                    {plan.name}
                  </motion.h3>
                  <motion.div 
                    className="flex items-baseline mb-6"
                    variants={{ hover: { scale: 1.05 } }}
                  >
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="ml-2 text-gray-500">initial</span>
                  </motion.div>
                  <div className="mb-8 text-sm text-gray-500">
                    Duration: {plan.duration}
                  </div>
                  <motion.ul 
                    className="space-y-4 mb-8"
                    variants={{
                      hover: { transition: { staggerChildren: 0.05 } }
                    }}
                  >
                    {plan.features.map((feature, idx) => (
                      <motion.li 
                        key={idx} 
                        className="flex items-center"
                        variants={{ hover: { x: 5 } }}
                      >
                        <motion.div
                          variants={{ hover: { rotate: 360, scale: 1.2 } }}
                          transition={{ duration: 0.3 }}
                        >
                          <CheckIcon className="h-5 w-5 text-primary-500 mr-2" />
                        </motion.div>
                        <span className="text-gray-600">{feature}</span>
                      </motion.li>
                    ))}
                  </motion.ul>
                  <div className="text-sm text-gray-500 mb-6">
                    Duration: {plan.duration}
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link
                      to="/contact"
                      className={`block w-full py-3 px-6 text-center rounded-lg font-medium transition-all duration-200 ${
                        plan.popular
                          ? 'bg-primary-600 text-white hover:bg-primary-700 hover:shadow-lg'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200 hover:shadow-md'
                      }`}
                    >
                      Get Started
                    </Link>
                  </motion.div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Benefits Section */}
      <motion.section 
        className="py-16 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Benefits of Investing With Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Retirement Planning",
                description: "Build a sustainable nest egg for your future with our smart investment strategies, ensuring a comfortable retirement lifestyle."
              },
              {
                title: "Financial Independence",
                description: "Take control of your financial future with investments that work harder than traditional savings methods."
              },
              {
                title: "Diversification & Risk Management",
                description: "Our platform offers diversified investment options tailored to minimize risk while maximizing potential returns."
              },
              {
                title: "Consistent Passive Income",
                description: "Generate reliable income streams through our investment strategies, reducing reliance on active work."
              },
              {
                title: "Expert Trading Analysis",
                description: "We utilize advanced technical analysis and market indicators to make informed trading decisions for optimal returns."
              },
              {
                title: "Compound Interest Growth",
                description: "Benefit from the power of compound interest, where your returns grow exponentially over time."
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                className="bg-gray-50 rounded-xl p-6 shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <h3 className="text-xl font-semibold mb-3 text-primary-900">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Risk Management Section */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-3xl font-bold mb-6 text-center">Our Risk Management Approach</h2>
            <p className="text-lg text-gray-600 mb-8 text-center max-w-3xl mx-auto">
              At QuickwealthINVEST, we understand that investing can feel intimidating due to the perceived risks involved. That's why we offer carefully designed investment strategies aimed at minimizing risk while maximizing potential returns.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-primary-900">Expert Analysis</h3>
                <p className="text-gray-600">Our team utilizes technical analysis, studying price charts and indicators, to make informed trading decisions. We monitor market sentiment and news to stay ahead of trends.</p>
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-primary-900">Diversified Portfolios</h3>
                <p className="text-gray-600">Your investments are spread across multiple assets, reducing the impact of any single underperforming investment while maximizing potential returns.</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Trading Process Section */}
      <motion.section 
        className="py-16 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">How Our Trading Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Trading Pairs Analysis",
                description: "We trade major cryptocurrency pairs like BTC/USD and ETH/BTC, analyzing market movements for optimal entry and exit points."
              },
              {
                title: "Advanced Risk Management",
                description: "We implement stop orders and strategic position sizing to protect investments and lock in profits."
              },
              {
                title: "Security First",
                description: "Your cryptocurrencies are stored securely using industry-leading security practices and protected storage solutions."
              }
            ].map((process, index) => (
              <motion.div
                key={index}
                className="bg-gray-50 rounded-xl p-6 shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <h3 className="text-xl font-semibold mb-3 text-primary-900">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Updated FAQ Section */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                q: "How do you manage investment risks?",
                a: "We employ diversification strategies, expert market analysis, and advanced risk management tools to protect your investments while maximizing potential returns."
              },
              {
                q: "What makes cryptocurrency a good investment?",
                a: "Cryptocurrency offers potential for high returns, acts as an inflation hedge, and provides portfolio diversification. Our expert management helps navigate market volatility."
              },
              {
                q: "How can I track my investment performance?",
                a: "Through our advanced dashboard, you can monitor your investments in real-time, view detailed analytics, and track your portfolio's performance."
              },
              {
                q: "What security measures do you have in place?",
                a: "We implement industry-leading security measures, including secure storage solutions and advanced encryption, to protect your investments."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-lg p-6 shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <h3 className="text-lg font-semibold mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Testimonials */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What Our Satisfied Clients Say
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover the success stories and experiences shared by our clients, showcasing 
              how our investment strategies have transformed their financial journeys.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-xl shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <p className="text-gray-600 mb-4">{testimonial.text}</p>
                <p className="font-medium text-gray-900">{testimonial.author}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section 
        className="py-16 bg-primary-900 text-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-8">Ready To Take Control Of Your Financial Future?</h2>
          <Link
            to="/contact"
            className="inline-block bg-white text-primary-900 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
          >
            Get Started
          </Link>
        </div>
      </motion.section>

      <Footer />
    </div>
  );
}

export default InvestmentPlans; 